import { memo, useRef } from "react";
import type { ID, ResourceResponse } from "types";
import { useTypedSelector, useResetState } from "hooks";
import { TableGridMode } from "consts/table";
import { FilterVariant } from "consts/filters";
import { Filters, MenuItem } from "consts/openBanking";
import { SetupIdentifier } from "consts/setups";
import { RequestQueryMapper } from "util/request-query-mapper";
import { isFormChanged } from "util/forms";
import {
    selectDomains,
    selectIsSetupsLoading
} from 'features/setups/selectors';
import { getIbanXsBanksRecipientsPivot } from "features/openBanking/api";
import { resetBankingSdkRecipientsState } from "features/openBanking/slice";
import type { IbanXsBank, IbanXsBanksRecipientsPivot } from "features/openBanking/types";
import {
    useBankingSDKRecipientsActions,
    useIbanXSBanksRecipientsPivotActions
} from "features/openBanking/actions";
import { IbanXSBanksRecipientsPivot } from "ui/forms/OpenBanking";
import { TabLayout } from "ui/widgets/StatefulTabs";
import { useResourceContext } from "ui/widgets/Resource";
import { ManageOpenBankingResourcePivot } from "ui/organizms/OpenBanking/shared";
import IbanXSBanksRecipientsPivotForm from "./IbanXSBanksRecipientsPivotForm";
import { IbanXSBanksRecipientsPivotFormContext } from "./IbanXSBanksRecipientsPivotFormContext";

const ManageIbanXSBanksRecipientsPivot = () => {
    const isBootstrapDataLoading = useTypedSelector(selectIsSetupsLoading(SetupIdentifier.Mors));

    const { resource } = useResourceContext<IbanXsBank>();

    const {
        createIbanXSBanksRecipientsPivot,
        deleteIbanXSBanksRecipientsPivot
    } = useIbanXSBanksRecipientsPivotActions();

    const {
        downloadCsv,
        getBankingSdkRecipients
    } = useBankingSDKRecipientsActions();

    const formProps: IbanXSBanksRecipientsPivot.IbanXSBanksRecipientsPivotForm = {
        isBootstrapDataLoading,
        bootstrapData: {
            MOR: useTypedSelector(selectDomains)
        },
        initialValues: IbanXSBanksRecipientsPivot.getInitialValues({}),
        onSaveOrCreate: async () => { }
    };

    const form = IbanXSBanksRecipientsPivot.useForm(formProps);

    const formRef = useRef(form);
    formRef.current = form;

    return (
        <TabLayout>
            <IbanXSBanksRecipientsPivotFormContext.Provider
                value={{
                    ...form,
                    ...formProps
                }}
            >
                <ManageOpenBankingResourcePivot
                    <IbanXsBank, IbanXsBanksRecipientsPivot>
                    TableProps={{
                        mode: TableGridMode.OpenBankingBankingSDKRecipients,
                        downloadCsv,
                        resetState: useResetState(resetBankingSdkRecipientsState),
                        fetchTableData: getBankingSdkRecipients,
                        filterTypes: new Map<FilterVariant, string[]>([
                            [FilterVariant.MultiOption, [
                                Filters.country,
                                Filters.accountCurrency
                            ]]
                        ])
                    }}
                    resource={resource}
                    menuItem={MenuItem.BankingSDKRecipients}
                    groupBy={({ recipientId }: IbanXsBanksRecipientsPivot) => recipientId}
                    fetchPivots={({ pki }) => getIbanXsBanksRecipientsPivot(
                        RequestQueryMapper.from()
                            .containsIn(Filters.bankId, `${pki}`)
                            .toString()
                    )}
                    AssociateActionProps={{
                        getOpenBankingResourcePivotActionProps: ({ id: recipientId }) => ({
                            ModalProps: {
                                titleSlot: 'Associate Iban XS with Recipient',
                                children: (
                                    <IbanXSBanksRecipientsPivotForm
                                        initialValues={{
                                            bankId: resource.pki,
                                            recipientId
                                        }}
                                    />
                                ),
                                onClose: () => formRef.current.resetForm()
                            },
                            TriggerComponentProps: {
                                TooltipProps: {
                                    title: `Associate Iban XS with Recipient ${recipientId}`
                                }
                            },
                            ActionPanelProps: {
                                SaveActionProps: {
                                    disabled: !formRef.current.isValid || !isFormChanged(formRef.current),
                                },
                                onSave: ({ pki }) => {
                                    const { values } = formRef.current;

                                    const [request] = createIbanXSBanksRecipientsPivot({
                                        isEnabled: values[IbanXSBanksRecipientsPivot.FormField.IsEnabled],
                                        MOR: values[IbanXSBanksRecipientsPivot.FormField.Mor],
                                        iso2: values[IbanXSBanksRecipientsPivot.FormField.Iso2].at(0),
                                        bankId: resource.pki,
                                        recipientId: pki
                                    });

                                    return request as Promise<ResourceResponse<IbanXsBanksRecipientsPivot>>;
                                }
                            }
                        })
                    }}
                    DissassociateActionProps={{
                        getOpenBankingResourcePivotActionProps: ({ pivotId }) => ({
                            ModalProps: {
                                titleSlot: 'Dissassociate Iban XS from Recipient',
                                children: 'Do you want to delete Iban XS Recipient Pivot ?'
                            },
                            TriggerComponentProps: {
                                TooltipProps: {
                                    title: `Delete Iban XS Banks Recipients Pivot ID ${pivotId}`
                                }
                            },
                            ActionPanelProps: {
                                onSave: ({ pki }) => {
                                    const [request] = deleteIbanXSBanksRecipientsPivot({
                                        coreId: pki
                                    });

                                    return request as Promise<ResourceResponse<ID>>;
                                }
                            }
                        })
                    }}
                />
            </IbanXSBanksRecipientsPivotFormContext.Provider>
        </TabLayout>
    );
};

export default memo(ManageIbanXSBanksRecipientsPivot);
